import * as THREE from 'three';

const ROCKET_SPEED = 38; // m/s
const HIT_RADIUS = 1.8;
const MAX_LIFETIME_SEC = 5;

const BODY_GEO = new THREE.CylinderGeometry(0.18, 0.18, 0.9, 10);
const NOSE_GEO = new THREE.ConeGeometry(0.18, 0.45, 10);
const FIN_GEO = new THREE.BoxGeometry(0.05, 0.3, 0.3);
const FLAME_GEO = new THREE.ConeGeometry(0.15, 0.6, 8);

const BODY_MAT = new THREE.MeshStandardMaterial({ color: 0xe5e7eb, roughness: 0.4, metalness: 0.5 });
const NOSE_MAT = new THREE.MeshStandardMaterial({ color: 0xef4444, roughness: 0.4 });
const FIN_MAT = new THREE.MeshStandardMaterial({ color: 0x1f2937, roughness: 0.6 });
const FLAME_MAT = new THREE.MeshStandardMaterial({
  color: 0xfb923c,
  emissive: 0xf97316,
  emissiveIntensity: 0.8,
});

// Homing rocket. Spawned at the firer's position, locks onto a target racer
// and steers toward them each frame. update() returns:
//   'hit'     — within HIT_RADIUS of the target this frame
//   'expired' — exceeded MAX_LIFETIME_SEC or target disappeared
//   null      — still flying
export class Rocket {
  constructor(scene, fromPos, targetRacerId) {
    this.targetId = targetRacerId;
    this.lifetime = 0;

    // Build a little rocket mesh oriented along +Z so we can lookAt the
    // target each frame.
    this.group = new THREE.Group();

    const body = new THREE.Mesh(BODY_GEO, BODY_MAT);
    body.rotation.x = Math.PI / 2;
    body.castShadow = true;
    this.group.add(body);

    const nose = new THREE.Mesh(NOSE_GEO, NOSE_MAT);
    nose.position.z = 0.65;
    nose.rotation.x = Math.PI / 2;
    nose.castShadow = true;
    this.group.add(nose);

    for (const side of [-1, 1]) {
      const fin = new THREE.Mesh(FIN_GEO, FIN_MAT);
      fin.position.set(side * 0.18, 0, -0.4);
      this.group.add(fin);
    }

    // Flame trail behind the rocket — a glowing cone.
    const flame = new THREE.Mesh(FLAME_GEO, FLAME_MAT);
    flame.position.z = -0.7;
    flame.rotation.x = -Math.PI / 2;
    this.group.add(flame);

    this.group.position.set(fromPos.x, fromPos.y + 1.2, fromPos.z);
    scene.add(this.group);
  }

  // Advance one frame. targetPose is the live pose of the target racer
  // (or null if they've gone). Returns the outcome string described above.
  update(dt, targetPose) {
    this.lifetime += dt;
    if (this.lifetime > MAX_LIFETIME_SEC || !targetPose) {
      return 'expired';
    }

    const tx = targetPose.position.x;
    const ty = (targetPose.position.y ?? 0) + 1.0;
    const tz = targetPose.position.z;

    const dx = tx - this.group.position.x;
    const dy = ty - this.group.position.y;
    const dz = tz - this.group.position.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq < HIT_RADIUS * HIT_RADIUS) {
      return 'hit';
    }

    const dist = Math.sqrt(distSq);
    const step = Math.min(dist, ROCKET_SPEED * dt);
    const nx = dx / dist;
    const ny = dy / dist;
    const nz = dz / dist;
    this.group.position.x += nx * step;
    this.group.position.y += ny * step;
    this.group.position.z += nz * step;

    // Orient the rocket along its flight direction (default mesh forward
    // is +Z so lookAt does what we want).
    this.group.lookAt(tx, ty, tz);

    return null;
  }

  destroy() {
    this.group.parent?.remove(this.group);
  }
}
